import React from 'react'
import styled from 'styled-components'

import { getToken as token } from 'theming'
import conditional, { whenProps } from 'tools/conditional'
import hoverable from 'styles/hoverable'
import focusable from 'styles/focusable'

import { BaseButton, IconButton as IconButtonDefault } from 'components/Button'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'

import type { PaginationItemProps } from './Pagination.types'
import { PAGINATION_ITEM_TYPE } from './Pagination.constants'

const IconButton = styled(IconButtonDefault)`
  margin: ${token('space-xs')};
`

const PageButton = styled(BaseButton)`
  background-color: transparent;
  border: none;

  ${focusable`
    box-shadow: ${token('button-primary-outline')};
  `}

  ${hoverable`
    * {
      text-decoration: underline;
      color: ${token('color-neutral-dark')};
    }
  `}

  ${Text} {
    color: ${conditional({
      'color-accent': whenProps({ 'aria-current': true }),
    })};
    text-decoration: ${conditional({
      underline: whenProps({ 'aria-current': true }),
    })};
  }
`

const ICONS_NAME: Record<string, string> = {
  previous: 'caret-left',
  next: 'caret-right',
}

function PaginationItem(props: PaginationItemProps): JSX.Element {
  const { itemType: type, page, ...others } = props

  const iconName = ICONS_NAME[type]

  if (iconName) {
    return (
      <IconButton {...others} id={iconName} aria-label={`Go to ${type} page`}>
        <Icon name={iconName} size={16} />
      </IconButton>
    )
  }

  return (
    <PageButton {...others} aria-label={`Go to page ${page?.toString() ?? ''}`}>
      <Text variant="body">
        {[PAGINATION_ITEM_TYPE.endEllipsis, PAGINATION_ITEM_TYPE.startEllipsis].includes(type)
          ? '...'
          : page}
      </Text>
    </PageButton>
  )
}

export default PaginationItem
